import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
} from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { MButton, MTextField, MAutocomplete, Loading } from "@app/components";
import useAuth from "@app/context/AuthContext";
import { errorAlert, successAlert } from "@app/utils/alerts";
import { postData } from "@services/utils/formHelpers";

interface BulkDuplicateModalProps {
  open: boolean;
  onClose: () => void;
  selectedSamples: number[];
  onSuccess: () => void;
}

interface BulkDuplicateFormValues {
  year: number;
}

const BulkDuplicateModal: React.FC<BulkDuplicateModalProps> = ({
  open,
  onClose,
  selectedSamples,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const currentYear = new Date().getFullYear();

  const form = useForm<BulkDuplicateFormValues>({
    defaultValues: {
      year: currentYear,
    },
  });

  const handleDuplicate = async (data: BulkDuplicateFormValues) => {
    if (selectedSamples.length === 0) {
      errorAlert("No hay muestras seleccionadas para duplicar");
      return;
    }

    // Prevenir múltiples envíos
    if (loading) {
      return;
    }

    setLoading(true);
    try {
      // Extraer el id si year es un objeto, o usar directamente si es número
      const yearValue = typeof data.year === 'object' && data.year !== null 
        ? (data.year as any).id 
        : data.year;

      const response = await postData("/sample-registration/bulk-duplicate", {
        sample_ids: selectedSamples,
        year: yearValue,
        analyst_id: user?.id,
      });

      if (response.success) {
        successAlert(
          `Se han duplicado exitosamente ${selectedSamples.length} muestra(s)`
        );
        onSuccess();
        handleClose();
      } else {
        errorAlert(response.error || "Error al duplicar las muestras");
      }
    } catch (error) {
      errorAlert("Error al duplicar las muestras");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const yearOptions: { id: number; name: string }[] = [];
  for (let i = currentYear - 5; i <= currentYear + 5; i++) {
    yearOptions.push({ id: i, name: i.toString() });
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Duplicación masiva de muestras</Typography>
      </DialogTitle>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleDuplicate)}>
          <DialogContent>
            {loading && <Loading />}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Se duplicarán <strong>{selectedSamples.length}</strong> muestra(s)
                seleccionada(s).
              </Typography>

              <Typography variant="subtitle2" sx={{ mb: 2, color: "primary.main" }}>
                Parámetros comunes para las muestras duplicadas:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <MAutocomplete
                    name="year"
                    label="Año que se indicarán en las muestras duplicadas"
                    options={yearOptions}
                    getOptionLabel={(option: any) => option.name || ""}

                  />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                    Seleccione el año para las muestras duplicadas
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    El analista será asignado automáticamente al usuario actual:{" "}
                    <strong>{user?.email}</strong>
                  </Typography>
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Características de la duplicación:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Solo se replicarán los datos del apartado de alta</li>
                  <li>La referencia de cliente quedará vacía</li>
                  <li>La acumulación tendrá el valor "N"</li>
                  <li>
                    No se duplicarán los datos de la fase de análisis
                  </li>
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <MButton
              variant="outlined"
              color="secondary"
              text="Cancelar"
              onClick={handleClose}
              disabled={loading}
            />
            <MButton
              variant="contained"
              color="primary"
              text="Duplicar"
              type="submit"
              disabled={loading}
            />
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default BulkDuplicateModal;
